import { useState } from 'react';
import { Notice } from '../../components/ui/Notice';
import { parseCalendarDate } from '../../lib/format';
import { todayCalendarDate } from '../backlog/sprint-form';
import { RELEASE_CONSEQUENCE } from './version-actions';
import type { Version } from './versions-api';

/**
 * RELEASE A VERSION — the confirmation, and the reason PH-28 is not four buttons.
 *
 * 🔴🔴 RELEASE IS EFFECTIVELY IRREVERSIBLE, AND THAT SENTENCE IS THE LARGEST THING ON THE PANEL,
 * BEFORE THE BUTTON THAT COMMITS IT. Exactly the shape `CompleteSprintDialog` uses, and for the same
 * reason — that panel exists because *"closing a sprint is irreversible … an action that does that
 * without saying so first is the defect"*.
 *
 * ⚠️ AND "IRREVERSIBLE" HERE IS MEASURED, NOT ASSUMED. `routes/versions.ts` serves release, archive
 * and unarchive and NOTHING that undoes a release; a second release answers 409 `is already
 * released`; and archive-then-unarchive does not undo it either, because `unarchiveVersion` derives
 * its destination from `release_date` and therefore returns a released version to RELEASED. ⛔ Do not
 * soften this to "you can archive it instead" — that is the trap the wording is written to close.
 *
 * ⭐ THE DATE IS A CHOICE, because the API makes it one, and it defaults to TODAY. An omitted date
 * means `current_date` in the SERVER's timezone; sending an explicit one means the day the user is
 * actually standing in decides, which is the number a release report shows months later.
 *
 * 🔴 MOBILE FIRST, 390px. Bottom sheet on a phone, centred from `sm:` up; the two buttons are 44px
 * and full-width-stacked below `sm:` so the confirm is not a small target beside a consequential one.
 */
export function ReleaseVersionDialog({
  version,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  version: Version;
  busy: boolean;
  /** The API's own words for a refusal. */
  error: string | null;
  onCancel: () => void;
  onConfirm: (releaseDate: string) => void;
}) {
  /**
   * ⭐ TODAY ON THE READER'S CALENDAR, from `todayCalendarDate` — which uses the LOCAL getters. ⛔ Not
   * `new Date().toISOString().slice(0,10)`: that is the UTC date, so in Asia/Bangkok every release
   * stamped before 07:00 would be dated YESTERDAY.
   * ⚠️ The version's PLANNED date is deliberately not the default. The plan and the day it shipped
   * are different facts, and defaulting to the plan is how a release page ends up showing an
   * intention as though it were history.
   */
  const [releaseDate, setReleaseDate] = useState(() => todayCalendarDate());
  const [problem, setProblem] = useState<string | null>(null);

  const submit = () => {
    if (!parseCalendarDate(releaseDate)) {
      // ⛔ Not sent to be refused: the API's `^\d{4}-\d{2}-\d{2}$` answers the fixed generic
      //    `the request body or query was not valid`, which names no field.
      setProblem('Choose the date this version actually shipped.');
      return;
    }
    setProblem(null);
    onConfirm(releaseDate);
  };

  const plannedDiffers = version.release_date !== null && version.release_date !== releaseDate;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Release ${version.name}`}
        className="max-h-full w-full max-w-lg space-y-3 overflow-y-auto rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">Release {version.name}?</h2>

        {/* 🔴 THE CONSEQUENCE, FIRST AND IN FULL. Not a tooltip, not fine print under the button. */}
        <p className="text-sm text-[color:var(--color-ink)]">{RELEASE_CONSEQUENCE}</p>

        <p className="text-xs text-[color:var(--color-ink-muted)]">
          The version becomes <strong>Released</strong> and drops below the unreleased ones in every
          picker. ⭐ Its tickets are not touched — neither <code>version</code> (planned for) nor{' '}
          <code>fix_version</code> (shipped in) changes, so the two counts on the card stay exactly as
          they are.
        </p>

        <div>
          <label className="block text-xs font-semibold" htmlFor="release-version-date">
            Released on
          </label>
          {/* 🔴 `type="date"` holding the API's own `YYYY-MM-DD` — no `Date` is constructed between
              this field and the request body. ⛔ `formatCalendarDate` here would render `1 Sept 2026`,
              which the input cannot hold; it would sanitise to `''` and show EMPTY. */}
          <input
            id="release-version-date"
            type="date"
            value={releaseDate}
            onChange={(event) => {
              setReleaseDate(event.target.value);
              setProblem(null);
            }}
            disabled={busy}
            className="mt-1 h-11 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            {plannedDiffers ? (
              <>
                ⚠️ This version was <strong>planned</strong> for {version.release_date}. The date
                above is the day it actually shipped and will replace it — the two are routinely
                different, and a release page that shows the plan as history is wrong in a way nobody
                can see.
              </>
            ) : (
              'Defaults to today, on your calendar. Backfilling an older release? Set the real day.'
            )}
          </p>
        </div>

        {problem ? (
          <Notice tone="warning" title="This release was not sent">
            <p className="break-words">{problem}</p>
          </Notice>
        ) : null}

        {/* ⛔ VERBATIM. `version 'v1.0.35' is already released` tells the reader somebody else got
            there first; "Could not release the version" tells them nothing and invites a second
            attempt that will fail identically. */}
        {error ? (
          <Notice tone="error" title="The API refused to release this version">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel — leave it unreleased
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Releasing…' : `Release ${version.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}
